/**
 * The capsule-id contract, in ONE place.
 *
 * It lives here rather than beside the journey resolver that first needed it
 * because the store now reads it too (the credential advance's compare-and-set),
 * and a reducer must not import a page module. Every consumer - the variant
 * resolver, the mission handoff, CredentialSpine, the level ordinal, the
 * terminal decision, and the advance reducer - goes through these two functions
 * so that a numeric string and a number can never describe two capsules.
 */

/**
 * coerceCapsuleId - the SINGLE capsule-id coercion. A numeric string ("501")
 * and a number (501) both coerce to the same positive integer; anything else ->
 * null. Sharing it closes the bug where resolveJourneyVariant accepted a
 * numeric-string id (Number("501") is valid) but CredentialSpine's own
 * Number.isInteger("501") check rejected it -> the spine fetched nothing and
 * hung on "loading" forever.
 *
 * @param {number|string|null} id
 * @returns {number|null}
 */
export function coerceCapsuleId(id) {
  const n = Number(id);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * sameCapsuleId - compare two capsule ids by value. Ids cross a JSON boundary,
 * so a numeric string and a number are the SAME capsule and must never look
 * like two. Two unusable ids are never "the same": null is an absence, not a
 * value, and treating it as one would let a malformed payload satisfy a
 * compare-and-set.
 *
 * @param {number|string|null} a
 * @param {number|string|null} b
 * @returns {boolean}
 */
export function sameCapsuleId(a, b) {
  const na = coerceCapsuleId(a);
  const nb = coerceCapsuleId(b);
  return na !== null && na === nb;
}
